import { useMemo } from 'react';
import { useFormikContext } from 'formik';
import SubmitButton from '@components/Modals/SubmitButton';
import { isFormikErrorsEmpty } from '@hooks/useFormikValues';
import { parseFormikErrors } from '@forms/types';

interface SubmitFormButtonProps {
	/** Name of the form element to submit on click */
	formName: string;
	/** Icon or element to display at the start of the submit button */
	startAdornment?: React.ReactNode;
	/** Text to display on the submit button. Default: 'Save' */
	submitButtonText?: string;
	/** Hovering the save button with pending changes will display this parameters */
	submitButtonMessage?: string;
	/** Whether the form is currently processing (submitting or thinking) */
	isProcessing?: boolean;
	/** Override default behavior of requiring at least 1 form change */
	enableSubmitAlways?: boolean;
	/** CSS class to apply to the submit button */
	className?: string;
}

export default function SubmitFormButton({ enableSubmitAlways, isProcessing, ...props }: SubmitFormButtonProps) {
	const { dirty, isSubmitting, errors } = useFormikContext();

	const isFormProcessing = useMemo(() => {
		return isSubmitting || isProcessing;
	}, [isSubmitting, isProcessing]);

	const errorDisplay = useMemo(() => {
		if (isFormikErrorsEmpty(errors)) return null;

		const errorList = parseFormikErrors(errors);
		return (
			<ul style={{ paddingLeft: '12px' }}>
				{errorList.map((error, index) => (
					<li key={`error-item-${String(index)}`}>{error}</li>
				))}
			</ul>
		);
	}, [errors]);

	const isSubmitDisabled = useMemo(() => {
		return (!enableSubmitAlways && !dirty) || !isFormikErrorsEmpty(errors);
	}, [dirty, enableSubmitAlways, errors]);

	return (
		<SubmitButton
			formName={props.formName}
			isSubmitting={isFormProcessing}
			isDisabled={isSubmitDisabled}
			disabledMessage={errorDisplay ?? 'No Changes Present'}
			buttonText={props.submitButtonText ? props.submitButtonText : 'Save'}
			submitMessage={props.submitButtonMessage ? props.submitButtonMessage : 'Save Changes'}
			startAdornment={props.startAdornment}
			className={props.className}
		/>
	);
}
